import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  UploadSimple, X, ShieldWarning, ShieldCheck, ArrowLeft, ArrowRight, CheckCircle,
  Sparkle, Star, ImageSquare, Tag, Info, Storefront, MapPin, Truck, Package,
  CurrencyDollar, ClockCounterClockwise, House, Car, Wrench, ShoppingBag, Factory,
  Toolbox, GraduationCap, Briefcase, PawPrint, HandHeart, HandCoins,
} from "@phosphor-icons/react";
import DopingUpsellModal, { upsellAlreadyShown } from "@/components/DopingUpsellModal";

// Category icon lookup (Phosphor name → component) — mirrors Home.jsx
const CAT_ICON_MAP = {
  House, Car, Wrench, ShoppingBag, Factory, Toolbox,
  GraduationCap, Briefcase, PawPrint, HandHeart, HandCoins, Storefront, Package,
};

const STEPS = [
  { id: "category", label: "Kategori" },
  { id: "photos", label: "Fotoğraflar" },
  { id: "details", label: "Ürün Bilgileri" },
  { id: "pricing", label: "Satış & Fiyat" },
  { id: "delivery", label: "Teslimat" },
  { id: "preview", label: "Önizleme" },
];

const fallbackImg = "https://images.unsplash.com/photo-1611095973362-88e8e2557e58?w=800&q=80";
const imgUrl = (p) => (!p ? fallbackImg : p.startsWith("http") ? p : fileUrl(p));

export default function CreateAuction() {
  const { user, loading } = useAuth();
  const nav = useNavigate();

  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState({
    title: "", description: "", category: "", subcategory: "",
    starting_price: "", min_increment: "10", duration_hours: "24",
    city: "", district: "", condition: "used", shipping_type: "kargo",
    listing_type: "auction", buy_now_price: "", accepts_offers: false,
  });
  // Wizard-only meta: sale_type UX abstraction over listing_type + buy_now_price
  // "auction" → listing_type=auction, buy_now null
  // "buy_now" → listing_type=open_ended, buy_now required
  // "both"    → listing_type=auction, buy_now required
  const [saleType, setSaleType] = useState("auction");
  const [images, setImages] = useState([]);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [upsellFor, setUpsellFor] = useState(null);

  useEffect(() => { if (!loading && !user) nav("/giris"); }, [loading, user, nav]);

  useEffect(() => {
    Promise.all([api.get("/categories"), api.get("/cities")]).then(([c, ci]) => {
      setCategories(c.data); setCities(ci.data);
    });
  }, []);

  const currentCat = useMemo(
    () => categories.find((c) => c.id === form.category),
    [categories, form.category]
  );

  // ─── Validation per step ─────────────────────────────────────────────
  const canProceed = (n = step) => {
    if (n === 0) return !!form.category;
    if (n === 1) return images.length >= 1;
    if (n === 2) return form.title.trim().length > 3 && form.description.trim().length > 10;
    if (n === 3) {
      if (saleType === "auction") {
        return Number(form.starting_price) > 0 && Number(form.min_increment) > 0 && Number(form.duration_hours) > 0;
      }
      if (saleType === "buy_now") {
        return Number(form.buy_now_price) > 0;
      }
      if (saleType === "both") {
        const sp = Number(form.starting_price), bn = Number(form.buy_now_price);
        return sp > 0 && bn > sp && Number(form.min_increment) > 0 && Number(form.duration_hours) > 0;
      }
      return false;
    }
    if (n === 4) return !!form.city && !!form.shipping_type;
    return true;
  };

  const stepError = () => {
    if (step === 0) return "Lütfen bir ana kategori seçin.";
    if (step === 1) return "En az 1 gerçek ürün görseli yükleyin.";
    if (step === 2) {
      if (form.title.trim().length <= 3) return "Başlık en az 4 karakter olmalı.";
      return "Açıklama en az 10 karakter olmalı.";
    }
    if (step === 3) {
      if (saleType === "buy_now") return "Geçerli bir Hemen Al fiyatı girin.";
      if (saleType === "both") {
        const sp = Number(form.starting_price), bn = Number(form.buy_now_price);
        if (bn <= sp) return "Hemen Al fiyatı başlangıç fiyatından yüksek olmalı.";
        return "Fiyat alanlarını doldurun.";
      }
      return "Başlangıç fiyatı, minimum artış ve süre girin.";
    }
    if (step === 4) return "İl ve kargo tipi zorunludur.";
    return null;
  };

  const goNext = () => {
    if (!canProceed()) { toast.error(stepError()); return; }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goPrev = () => {
    setStep((s) => Math.max(0, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ─── Photo upload ─────────────────────────────────────────────────────
  const handleUploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    if (images.length + files.length > 10) return toast.error("En fazla 10 görsel yükleyebilirsin");
    setUploading(true);
    try {
      for (const f of files) {
        if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name} 8MB'den büyük, atlandı`); continue; }
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, { path: data.path, previewUrl: URL.createObjectURL(f), name: f.name }]);
      }
      toast.success("Görseller yüklendi");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Yükleme başarısız");
    } finally { setUploading(false); }
  };
  const handleUpload = async (e) => { await handleUploadFiles(e.target.files); e.target.value = ""; };
  const removeImg = (i) => setImages((prev) => prev.filter((_, idx) => idx !== i));
  const moveImg = (from, to) => {
    if (to < 0 || to >= images.length) return;
    setImages((prev) => {
      const next = [...prev]; const [item] = next.splice(from, 1); next.splice(to, 0, item); return next;
    });
  };
  const setCover = (i) => moveImg(i, 0);

  // ─── Publish ─────────────────────────────────────────────────────────
  const publish = async () => {
    if (!canProceed(0) || !canProceed(1) || !canProceed(2) || !canProceed(3) || !canProceed(4)) {
      toast.error("Tüm adımları tamamlayın."); return;
    }
    setSubmitting(true);
    try {
      const isBuyNowOnly = saleType === "buy_now";
      const payload = {
        ...form,
        subcategory: form.subcategory || null,
        listing_type: isBuyNowOnly ? "open_ended" : "auction",
        starting_price: isBuyNowOnly ? Number(form.buy_now_price) : Number(form.starting_price),
        min_increment: Number(form.min_increment || 10),
        duration_hours: isBuyNowOnly ? 1 : Number(form.duration_hours),
        buy_now_price: (saleType === "buy_now" || saleType === "both")
          ? Number(form.buy_now_price)
          : null,
        images: images.map((i) => i.path),
      };
      const { data } = await api.post("/auctions", payload);
      toast.success("İlanınız oluşturuldu ve inceleme sürecine alındı.");
      if (!upsellAlreadyShown(data.id)) {
        setUpsellFor({ id: data.id, title: data.title || payload.title });
      } else {
        nav(`/mezat/${data.id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Oluşturulamadı");
    } finally { setSubmitting(false); }
  };

  // ─── Not verified gate ────────────────────────────────────────────────
  if (user && !user.is_verified) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16" data-testid="verify-required">
        <div className="rounded-2xl bg-white premium-border p-8 flex items-start gap-4">
          <ShieldWarning size={28} weight="fill" className="text-amber-700 shrink-0" />
          <div className="flex-1">
            <h2 className="font-display text-2xl font-medium mb-2 tracking-tight">Önce kimliğini doğrula</h2>
            <p className="text-sm text-slate-600 mb-4 leading-relaxed">İlan açabilmek için TCKN doğrulamasını (simülasyon) tamamlamanız gerekir.</p>
            <button onClick={() => nav("/dogrula")} className="bg-slate-900 text-white px-5 py-3 rounded-full font-medium hover:bg-slate-800 transition-colors" data-testid="go-verify-btn">
              Kimliği Doğrula
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading || !user) return null;

  // ─── Render ─────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50/40" data-testid="create-auction-page">
      {upsellFor && (
        <DopingUpsellModal
          listingId={upsellFor.id}
          listingTitle={upsellFor.title}
          onClose={() => { const id = upsellFor.id; setUpsellFor(null); nav(`/mezat/${id}`); }}
        />
      )}

      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-12">
        {/* Header */}
        <div className="mb-8">
          <p className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-2">Yeni ilan</p>
          <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight text-slate-900">
            Adım adım <span className="italic text-slate-500">hazırlayın</span>
          </h1>
        </div>

        {/* Stepper */}
        <Stepper current={step} onJump={(i) => i < step && setStep(i)} />

        {/* Content grid — main + summary */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
          <main className="lg:col-span-2 min-w-0">
            {step === 0 && (
              <CategoryStep categories={categories} form={form} setForm={setForm} />
            )}
            {step === 1 && (
              <PhotosStep
                images={images} uploading={uploading} dragOver={dragOver} setDragOver={setDragOver}
                handleUpload={handleUpload} handleUploadFiles={handleUploadFiles}
                removeImg={removeImg} moveImg={moveImg} setCover={setCover}
              />
            )}
            {step === 2 && (
              <DetailsStep form={form} setForm={setForm} images={images} />
            )}
            {step === 3 && (
              <PricingStep form={form} setForm={setForm} saleType={saleType} setSaleType={setSaleType} />
            )}
            {step === 4 && (
              <DeliveryStep form={form} setForm={setForm} cities={cities} />
            )}
            {step === 5 && (
              <PreviewStep form={form} images={images} saleType={saleType} categories={categories} />
            )}

            {/* Navigation */}
            <div className="mt-10 flex items-center justify-between gap-3 pb-24 md:pb-0">
              <button
                type="button" onClick={goPrev} disabled={step === 0}
                data-testid="prev-step-button"
                className="px-5 py-3 rounded-full font-medium text-sm text-slate-700 border border-slate-200 hover:border-slate-900 hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <ArrowLeft size={14} weight="bold" /> Geri
              </button>

              {step < STEPS.length - 1 ? (
                <button
                  type="button" onClick={goNext}
                  data-testid="next-step-button"
                  disabled={!canProceed()}
                  className="px-6 py-3 rounded-full font-medium text-sm bg-slate-900 text-white hover:bg-black transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Devam et <ArrowRight size={14} weight="bold" />
                </button>
              ) : (
                <button
                  type="button" onClick={publish}
                  data-testid="publish-listing-button"
                  disabled={submitting || uploading}
                  className="px-6 py-3 rounded-full font-medium text-sm bg-slate-900 text-white hover:bg-black transition-colors flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <CheckCircle size={16} weight="fill" /> {submitting ? "Yayınlanıyor…" : "Yayınla"}
                </button>
              )}
            </div>
          </main>

          {/* Sticky summary panel (desktop) */}
          <aside className="hidden lg:block">
            <SummaryPanel form={form} images={images} saleType={saleType} categories={categories} />
          </aside>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// STEPPER
// ═══════════════════════════════════════════════════════════════════════
function Stepper({ current, onJump }) {
  return (
    <>
      {/* Desktop stepper */}
      <div className="hidden md:flex items-center gap-3" data-testid="create-stepper">
        {STEPS.map((s, i) => (
          <div key={s.id} className="flex items-center flex-1 last:flex-none">
            <button
              type="button"
              onClick={() => onJump(i)}
              disabled={i > current}
              data-testid={`step-${s.id}`}
              className={`flex items-center gap-2.5 min-w-0 disabled:cursor-not-allowed ${i <= current ? "" : "opacity-50"}`}
            >
              <span
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-mono-tight font-medium shrink-0 transition-colors ${
                  i < current ? "bg-emerald-600 text-white"
                  : i === current ? "bg-slate-900 text-white"
                  : "bg-white premium-border text-slate-500"
                }`}
              >
                {i < current ? <CheckCircle size={14} weight="fill" /> : i + 1}
              </span>
              <span className={`text-xs font-medium truncate ${i === current ? "text-slate-900" : "text-slate-500"}`}>
                {s.label}
              </span>
            </button>
            {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-2 ${i < current ? "bg-emerald-600" : "bg-slate-200"}`} />}
          </div>
        ))}
      </div>

      {/* Mobile compact */}
      <div className="md:hidden flex items-center justify-between" data-testid="create-stepper-mobile">
        <span className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-500">
          Adım {current + 1}/{STEPS.length}
        </span>
        <span className="font-display text-lg font-medium text-slate-900">{STEPS[current].label}</span>
      </div>
      <div className="md:hidden mt-3 h-1 bg-slate-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-slate-900 transition-all duration-500 rounded-full"
          style={{ width: `${((current + 1) / STEPS.length) * 100}%` }}
        />
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// STEPS
// ═══════════════════════════════════════════════════════════════════════

function StepShell({ testId, title, subtitle, help, children }) {
  return (
    <div className="rounded-2xl bg-white premium-border p-6 md:p-8" data-testid={testId}>
      <div className="mb-6">
        <h2 className="font-display text-2xl font-medium tracking-tight text-slate-900">{title}</h2>
        {subtitle && <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{subtitle}</p>}
      </div>
      {children}
      {help && (
        <p className="text-[11px] text-slate-400 mt-6 flex items-start gap-1.5 leading-relaxed">
          <Info size={11} className="mt-0.5 shrink-0" /> {help}
        </p>
      )}
    </div>
  );
}

function CategoryStep({ categories, form, setForm }) {
  const currentCat = categories.find((c) => c.id === form.category);
  return (
    <StepShell
      testId="step-category-content"
      title="Kategori seçin"
      subtitle="Doğru kategori, ilanınızın daha hızlı bulunmasını sağlar."
      help="Yanlış kategori seçerseniz sonraki adımlarda değiştirebilirsiniz."
    >
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6" data-testid="category-grid">
        {categories.slice(0, 12).map((c) => {
          const Icon = CAT_ICON_MAP[c.icon] || Package;
          const selected = form.category === c.id;
          return (
            <button
              key={c.id}
              type="button"
              data-testid={`cat-option-${c.id}`}
              onClick={() => setForm({ ...form, category: c.id, subcategory: "" })}
              className={`p-4 rounded-xl flex flex-col items-start gap-2.5 transition-all text-left min-h-[100px] ${
                selected
                  ? "bg-slate-900 text-white ring-2 ring-slate-900"
                  : "bg-white premium-border hover-lift"
              }`}
            >
              <div className={`w-9 h-9 rounded-full flex items-center justify-center ${selected ? "bg-white/10" : "bg-slate-50"}`}>
                <Icon size={16} weight="regular" className={selected ? "text-white" : "text-slate-700"} />
              </div>
              <span className="font-display text-sm font-medium leading-snug">{c.name}</span>
            </button>
          );
        })}
      </div>

      {currentCat && currentCat.subcategories?.length > 0 && (
        <div className="pt-6 border-t divider">
          <p className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-500 mb-3">
            Alt kategori (opsiyonel)
          </p>
          <div className="flex flex-wrap gap-2" data-testid="subcategory-chips">
            {currentCat.subcategories.map((s) => {
              const active = form.subcategory === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  data-testid={`subcat-option-${s.id}`}
                  onClick={() => setForm({ ...form, subcategory: active ? "" : s.id })}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    active
                      ? "bg-slate-900 text-white"
                      : "bg-white premium-border text-slate-700 hover:border-slate-900"
                  }`}
                >
                  {s.name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </StepShell>
  );
}

function PhotosStep({ images, uploading, dragOver, setDragOver, handleUpload, handleUploadFiles, removeImg, moveImg, setCover }) {
  return (
    <StepShell
      testId="step-photos-content"
      title="Fotoğraflar"
      subtitle="En az 1 gerçek fotoğraf yükleyin. Kapak fotoğrafı ilk izlenimi belirler."
      help="Net, aydınlık ve gerçek ürün fotoğrafları ilanınızın daha hızlı ilgi görmesini sağlar. JPG/PNG/WEBP · min 400×400 · max 8MB · en fazla 10 görsel."
    >
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="image-grid">
          {images.map((im, i) => (
            <div
              key={im.path}
              data-testid={`image-item-${i}`}
              className={`relative aspect-square rounded-xl overflow-hidden group ${i === 0 ? "ring-2 ring-slate-900 ring-offset-2" : "premium-border"}`}
            >
              <img src={im.previewUrl} alt="" className="w-full h-full object-cover" />
              {i === 0 && (
                <span className="absolute top-2 left-2 bg-slate-900 text-white text-[9px] uppercase tracking-[0.18em] font-medium px-2 py-0.5 rounded-full">
                  Kapak
                </span>
              )}
              <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                {i > 0 && (
                  <button
                    type="button" onClick={() => setCover(i)}
                    data-testid={`cover-image-button-${i}`}
                    className="bg-white text-slate-900 text-[10px] uppercase tracking-[0.16em] font-medium px-2 py-1 rounded"
                  >Kapak yap</button>
                )}
                <button type="button" onClick={() => moveImg(i, i - 1)} disabled={i === 0}
                  data-testid={`image-left-${i}`}
                  className="bg-white text-slate-900 w-7 h-7 rounded disabled:opacity-40 flex items-center justify-center">←</button>
                <button type="button" onClick={() => moveImg(i, i + 1)} disabled={i === images.length - 1}
                  data-testid={`image-right-${i}`}
                  className="bg-white text-slate-900 w-7 h-7 rounded disabled:opacity-40 flex items-center justify-center">→</button>
                <button type="button" onClick={() => removeImg(i)}
                  data-testid={`image-remove-${i}`}
                  className="bg-[#B54037] text-white w-7 h-7 rounded flex items-center justify-center">
                  <X size={11} weight="bold" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div
        className={`rounded-xl bg-white p-4 transition-all ${dragOver ? "ring-2 ring-slate-900 bg-slate-50" : "premium-border"}`}
        data-testid="photo-upload-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleUploadFiles(e.dataTransfer.files); }}
      >
        <label className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg cursor-pointer py-10 md:py-14 transition-colors ${dragOver ? "border-slate-900 bg-slate-50" : "border-slate-200 bg-slate-50/50 hover:border-slate-900 hover:bg-slate-50"}`}>
          <ImageSquare size={26} weight="regular" className="text-slate-500" />
          <span className="text-sm font-medium text-slate-800">
            {uploading ? "Yükleniyor…" : dragOver ? "Buraya bırak" : "Fotoğraf seç veya sürükle bırak"}
          </span>
          <span className="text-xs text-slate-500">JPG, PNG, WEBP · max 8MB · min 400×400</span>
          <input type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" data-testid="input-file" />
        </label>
      </div>

      {images.length > 0 && (
        <p className="text-[11px] text-slate-500 mt-4 leading-relaxed">
          💡 <strong>{images[0]?.name || "İlk görsel"}</strong> kapak olarak seçildi. Sırayı değiştirmek için görsele hover edin.
        </p>
      )}
    </StepShell>
  );
}

function DetailsStep({ form, setForm, images }) {
  return (
    <StepShell
      testId="step-details-content"
      title="Ürün bilgileri"
      subtitle="Detaylı ve dürüst açıklama güven artırır, ilanınız daha hızlı satılır."
    >
      {/* Title */}
      <div className="mb-6">
        <label className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-500 block mb-2">
          Başlık · <span className="text-slate-400 normal-case tracking-normal">{form.title.length}/200</span>
        </label>
        <input
          data-testid="input-title" maxLength={200}
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="Örn: Temiz iPhone 14 Pro Max 256 GB · Kutulu, faturalı"
          className="w-full bg-white premium-border rounded-xl px-4 py-3 text-base outline-none focus:border-slate-900 transition-colors"
        />
      </div>

      {/* Description */}
      <div className="mb-6">
        <label className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-500 block mb-2">
          Açıklama
        </label>
        <textarea
          data-testid="input-description" rows={6}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Ürünün durumu, kullanım süresi, varsa kusurları ve teslimat detaylarını yazın."
          className="w-full bg-white premium-border rounded-xl px-4 py-3 text-base outline-none focus:border-slate-900 transition-colors min-h-[160px] resize-y"
        />
        <p className="text-[11px] text-slate-400 mt-2">Kusurları açıkça yazmak güveni artırır.</p>
      </div>

      {/* Condition — radio cards */}
      <div className="mb-6">
        <label className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-500 block mb-3">
          Ürün durumu
        </label>
        <div className="grid grid-cols-2 gap-3" data-testid="condition-cards">
          {[
            { id: "new", label: "Sıfır", desc: "Hiç kullanılmamış, kutusunda" },
            { id: "used", label: "İkinci El", desc: "Kullanılmış, iyi durumda" },
          ].map((c) => {
            const selected = form.condition === c.id;
            return (
              <button
                key={c.id} type="button"
                data-testid={`condition-${c.id}`}
                onClick={() => setForm({ ...form, condition: c.id })}
                className={`p-4 rounded-xl text-left transition-all ${
                  selected
                    ? "bg-slate-900 text-white ring-2 ring-slate-900"
                    : "bg-white premium-border hover:border-slate-900"
                }`}
              >
                <p className="font-medium text-sm">{c.label}</p>
                <p className={`text-xs mt-1 ${selected ? "text-white/70" : "text-slate-500"}`}>{c.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* AI assistant — collapsible */}
      <AIAssistant form={form} setForm={setForm} images={images} />
    </StepShell>
  );
}

function PricingStep({ form, setForm, saleType, setSaleType }) {
  const isAuction = saleType === "auction";
  const isBuyNow = saleType === "buy_now";
  const isBoth = saleType === "both";

  return (
    <StepShell
      testId="step-pricing-content"
      title="Satış tipi ve fiyat"
      subtitle="Alıcının ilanınızı nasıl satın alacağını belirleyin."
      help="Gerçekçi fiyat daha hızlı teklif alır."
    >
      {/* Sale type — 3 cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" data-testid="sale-type-cards">
        {[
          { id: "auction", label: "Açık artırma", desc: "Alıcılar teklif verir, süre sonunda en yüksek teklif kazanır." },
          { id: "buy_now", label: "Hemen Al", desc: "Alıcı belirlediğiniz fiyattan doğrudan satın alabilir." },
          { id: "both", label: "İkisi birlikte", desc: "Alıcı ister teklif verir, ister Hemen Al fiyatıyla alır." },
        ].map((t) => {
          const selected = saleType === t.id;
          return (
            <button
              key={t.id} type="button"
              data-testid={`sale-type-${t.id}`}
              onClick={() => setSaleType(t.id)}
              className={`p-5 rounded-xl text-left transition-all ${
                selected
                  ? "bg-slate-900 text-white ring-2 ring-slate-900"
                  : "bg-white premium-border hover:border-slate-900"
              }`}
            >
              <p className="font-display text-base font-medium tracking-tight">{t.label}</p>
              <p className={`text-xs mt-1.5 leading-relaxed ${selected ? "text-white/70" : "text-slate-500"}`}>{t.desc}</p>
            </button>
          );
        })}
      </div>

      {/* Dynamic price fields */}
      {(isAuction || isBoth) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <PriceInput
            testId="input-starting-price"
            label="Başlangıç fiyatı (₺)"
            value={form.starting_price}
            onChange={(v) => setForm({ ...form, starting_price: v })}
          />
          <PriceInput
            testId="input-min-increment"
            label="Minimum artış (₺)"
            value={form.min_increment}
            onChange={(v) => setForm({ ...form, min_increment: v })}
          />
          <PriceInput
            testId="input-duration"
            label="Süre (saat)"
            value={form.duration_hours}
            onChange={(v) => setForm({ ...form, duration_hours: v })}
            step="1" min="1" max="720"
          />
        </div>
      )}

      {(isBuyNow || isBoth) && (
        <div className={`grid grid-cols-1 ${isBoth ? "md:grid-cols-2" : "md:grid-cols-1"} gap-4 mt-4`}>
          <PriceInput
            testId="input-buy-now"
            label={isBoth ? "Hemen Al fiyatı (₺)" : "Hemen Al fiyatı (₺)"}
            value={form.buy_now_price}
            onChange={(v) => setForm({ ...form, buy_now_price: v })}
            hint={isBoth ? "Başlangıç fiyatından yüksek olmalı." : null}
          />
          {isBuyNow && (
            <label className="flex items-center gap-3 bg-slate-50 px-4 py-3 rounded-xl cursor-pointer premium-border mt-0 md:mt-0">
              <input type="checkbox" checked={form.accepts_offers}
                onChange={(e) => setForm({ ...form, accepts_offers: e.target.checked })}
                data-testid="input-accepts-offers" className="w-4 h-4" />
              <span className="text-sm text-slate-700">Alıcılar fiyat/takas teklifi gönderebilsin</span>
            </label>
          )}
        </div>
      )}
    </StepShell>
  );
}

function PriceInput({ testId, label, value, onChange, step = "0.01", min = "1", max, hint }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-500 block mb-2">{label}</label>
      <input
        data-testid={testId} type="number"
        step={step} min={min} max={max}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white premium-border rounded-xl px-4 py-3 font-mono-tight text-base outline-none focus:border-slate-900 transition-colors"
      />
      {hint && <p className="text-[11px] text-slate-500 mt-1.5">{hint}</p>}
    </div>
  );
}

function DeliveryStep({ form, setForm, cities }) {
  return (
    <StepShell
      testId="step-delivery-content"
      title="Teslimat ve konum"
      subtitle="Alıcı ürünü nereden ve nasıl teslim alacak?"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-500 block mb-2">İl *</label>
          <select
            data-testid="input-city"
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
            className="w-full bg-white premium-border rounded-xl px-4 py-3 text-base outline-none focus:border-slate-900 transition-colors"
          >
            <option value="">İl seçin…</option>
            {cities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-500 block mb-2">İlçe</label>
          <input
            data-testid="input-district"
            value={form.district}
            onChange={(e) => setForm({ ...form, district: e.target.value })}
            placeholder="Örn: Kadıköy"
            className="w-full bg-white premium-border rounded-xl px-4 py-3 text-base outline-none focus:border-slate-900 transition-colors"
          />
        </div>
      </div>

      <div>
        <label className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-500 block mb-3">Teslimat şekli *</label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="shipping-cards">
          {[
            { id: "kargo", label: "Kargo (alıcı öder)", icon: Truck, desc: "Alıcı kargo bedelini karşılar" },
            { id: "dahil", label: "Kargo dahil", icon: Package, desc: "Kargo fiyata dahil" },
            { id: "elden", label: "Elden teslim", icon: HandHeart, desc: "Alıcı ile yüz yüze" },
          ].map((s) => {
            const selected = form.shipping_type === s.id;
            return (
              <button
                key={s.id} type="button"
                data-testid={`shipping-${s.id}`}
                onClick={() => setForm({ ...form, shipping_type: s.id })}
                className={`p-4 rounded-xl text-left transition-all ${
                  selected ? "bg-slate-900 text-white ring-2 ring-slate-900" : "bg-white premium-border hover:border-slate-900"
                }`}
              >
                <s.icon size={16} weight="regular" className="mb-2" />
                <p className="font-medium text-sm">{s.label}</p>
                <p className={`text-xs mt-0.5 ${selected ? "text-white/70" : "text-slate-500"}`}>{s.desc}</p>
              </button>
            );
          })}
        </div>
      </div>
    </StepShell>
  );
}

function PreviewStep({ form, images, saleType, categories }) {
  const cat = categories.find((c) => c.id === form.category);
  const subcat = cat?.subcategories?.find((s) => s.id === form.subcategory);
  const saleLabel = saleType === "auction" ? "Açık artırma"
    : saleType === "buy_now" ? "Sadece Hemen Al" : "Açık artırma + Hemen Al";

  return (
    <StepShell
      testId="step-preview-content"
      title="Önizleme ve yayınla"
      subtitle="Yayınlamadan önce ilanınızı son kez kontrol edin."
      help="Yayınla dediğinizde ilan AI moderasyon sürecine alınır. Onaylandığında keşfet sayfasında görünür."
    >
      {/* Listing-card style preview */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6" data-testid="listing-preview-card">
        <div className="md:col-span-2">
          <div className="rounded-2xl overflow-hidden bg-slate-50 aspect-[5/4]">
            <img
              src={images[0] ? images[0].previewUrl : fallbackImg}
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          {images.length > 1 && (
            <div className="grid grid-cols-5 gap-1.5 mt-2">
              {images.slice(0, 5).map((im, i) => (
                <div key={i} className={`aspect-square rounded-md overflow-hidden ${i === 0 ? "ring-1 ring-slate-900" : "opacity-70"}`}>
                  <img src={im.previewUrl} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="md:col-span-3 space-y-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400 mb-1.5">
              {cat?.name}{subcat ? ` · ${subcat.name}` : ""}
            </p>
            <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight text-slate-900 leading-tight">
              {form.title || <span className="text-slate-400 italic">Başlık girilmedi</span>}
            </h3>
          </div>

          <p className="text-sm text-slate-600 leading-relaxed line-clamp-4">
            {form.description || <span className="text-slate-400 italic">Açıklama girilmedi</span>}
          </p>

          <div className="grid grid-cols-2 gap-4 pt-4 border-t divider">
            <PreviewRow label="Satış tipi" value={saleLabel} />
            {(saleType === "auction" || saleType === "both") && (
              <>
                <PreviewRow label="Başlangıç" value={`₺${Number(form.starting_price || 0).toLocaleString("tr-TR")}`} />
                <PreviewRow label="Süre" value={`${form.duration_hours} saat`} />
              </>
            )}
            {(saleType === "buy_now" || saleType === "both") && (
              <PreviewRow label="Hemen Al" value={`₺${Number(form.buy_now_price || 0).toLocaleString("tr-TR")}`} />
            )}
            <PreviewRow label="Durum" value={form.condition === "new" ? "Sıfır" : "İkinci El"} />
            <PreviewRow label="Şehir" value={`${form.city || "–"}${form.district ? ` · ${form.district}` : ""}`} />
            <PreviewRow label="Teslimat" value={({ kargo: "Kargo (alıcı öder)", dahil: "Kargo dahil", elden: "Elden teslim" })[form.shipping_type]} />
            <PreviewRow label="Görsel" value={`${images.length} adet`} />
          </div>
        </div>
      </div>

      <div className="mt-8 pt-6 border-t divider">
        <p className="text-[11px] text-slate-500 flex items-start gap-2 leading-relaxed">
          <ShieldCheck size={12} weight="fill" className="text-emerald-700 shrink-0 mt-0.5" />
          <span>İlan yayınlandıktan sonra <strong>AI moderasyon</strong> sürecine alınır. Yasaklı içerik, telefon/email veya dış link tespit edilirse manuel incelemeye düşer. Güvenli, dürüst ilanlar birkaç saniye içinde onaylanır.</span>
        </p>
      </div>
    </StepShell>
  );
}

function PreviewRow({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400 mb-1">{label}</p>
      <p className="text-sm font-medium text-slate-800">{value}</p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SUMMARY PANEL (desktop right side)
// ═══════════════════════════════════════════════════════════════════════
function SummaryPanel({ form, images, saleType, categories }) {
  const cat = categories.find((c) => c.id === form.category);
  const saleLabel = saleType === "auction" ? "Açık artırma"
    : saleType === "buy_now" ? "Hemen Al" : "Açık artırma + Hemen Al";

  return (
    <div className="sticky top-24 rounded-2xl bg-white premium-border p-6 space-y-5" data-testid="listing-summary-card">
      <p className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-500">İlan özeti</p>

      {/* Cover preview */}
      <div className="rounded-xl overflow-hidden aspect-[5/4] bg-slate-50">
        <img
          src={images[0]?.previewUrl || fallbackImg}
          alt=""
          className="w-full h-full object-cover"
          onError={(e) => (e.target.src = fallbackImg)}
        />
      </div>

      <div className="space-y-3 text-sm">
        <SummaryRow icon={Tag} label="Kategori" value={cat?.name || "–"} />
        <SummaryRow icon={ImageSquare} label="Görsel" value={images.length > 0 ? `${images.length} adet` : "–"} />
        <SummaryRow icon={CurrencyDollar} label="Satış" value={form.category ? saleLabel : "–"} />
        <SummaryRow
          icon={ClockCounterClockwise}
          label={saleType === "buy_now" ? "Fiyat" : "Başlangıç"}
          value={
            saleType === "buy_now"
              ? (form.buy_now_price ? `₺${Number(form.buy_now_price).toLocaleString("tr-TR")}` : "–")
              : (form.starting_price ? `₺${Number(form.starting_price).toLocaleString("tr-TR")}` : "–")
          }
        />
        <SummaryRow icon={MapPin} label="Şehir" value={form.city || "–"} />
      </div>

      <p className="text-[11px] text-slate-500 flex items-start gap-1.5 leading-relaxed pt-3 border-t divider">
        <Info size={11} className="mt-0.5 shrink-0" />
        Her adımı tamamladıkça bu özet güncellenir.
      </p>
    </div>
  );
}

function SummaryRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-7 h-7 rounded-full bg-slate-50 flex items-center justify-center shrink-0">
        <Icon size={13} className="text-slate-600" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400 leading-none mb-1">{label}</p>
        <p className="text-sm font-medium text-slate-800 truncate">{value}</p>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// AI ASSISTANT (kept — refined UI)
// ═══════════════════════════════════════════════════════════════════════
function AIAssistant({ form, setForm, images }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/ai/generate-listing", {
        note,
        category: form.category || null,
        has_photos: (images || []).length > 0,
        hint_price: form.starting_price ? parseFloat(form.starting_price) : null,
      });
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI önerisi alınamadı, manuel devam edebilirsiniz.");
    } finally { setBusy(false); }
  };

  const apply = () => {
    if (!result) return;
    setForm((f) => ({
      ...f,
      title: result.title || f.title,
      description: result.description || f.description,
      category: result.suggested_category || f.category,
      starting_price: result.price_range?.min ? String(result.price_range.min) : f.starting_price,
      listing_type: result.suggested_listing_type || f.listing_type,
    }));
    toast.success("AI önerileri forma uygulandı");
  };

  const riskColor = { low: "text-emerald-800", medium: "text-amber-700", high: "text-[#B54037]" };

  return (
    <div className="mt-8 p-5 rounded-xl bg-slate-50 space-y-3" data-testid="ai-assistant">
      <div className="flex items-center gap-2">
        <Sparkle size={14} weight="fill" className="text-slate-500" />
        <p className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-500 flex-1">
          AI ilan asistanı · yardımcı aksiyon
        </p>
      </div>
      <p className="text-xs text-slate-500 leading-relaxed">
        Kısaca ürünü anlatın, AI sizin için başlık, açıklama ve fiyat önerisi hazırlasın.
      </p>
      <div className="flex gap-2 flex-wrap">
        <input
          data-testid="ai-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Örn: iPhone 13 Pro 256GB, temiz, faturalı, kutulu"
          className="flex-1 min-w-[240px] bg-white premium-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-slate-900"
        />
        <button
          type="button" onClick={generate} disabled={busy || !note.trim()}
          data-testid="ai-generate"
          className="bg-slate-900 text-white px-5 py-2.5 rounded-full font-medium text-sm hover:bg-black transition-colors disabled:opacity-60"
        >
          {busy ? "Hazırlanıyor…" : "Üret"}
        </button>
      </div>

      {result && (
        <div className="mt-2 p-4 rounded-lg bg-white premium-border space-y-3 text-sm" data-testid="ai-result">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400">Önerilen başlık</p>
            <p className="font-medium text-slate-900">{result.title}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400">Açıklama</p>
            <p className="text-slate-700 whitespace-pre-line text-xs leading-relaxed">
              {result.description?.slice(0, 300)}{result.description?.length > 300 ? "…" : ""}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400">Fiyat aralığı</p>
              <p className="font-mono-tight text-slate-900 text-xs">
                ₺{result.price_range?.min?.toLocaleString("tr-TR")} — ₺{result.price_range?.max?.toLocaleString("tr-TR")}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400">Önerilen tip</p>
              <p className="font-medium text-slate-900 text-xs">
                {result.suggested_listing_type === "auction" ? "Açık artırma" : "Süresiz"}
              </p>
            </div>
          </div>
          {result.risk && (
            <div className="pt-2 border-t divider">
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400">AI risk analizi</p>
              <p className={`text-xs font-medium ${riskColor[result.risk.ai_risk_level] || "text-slate-700"}`}>
                Seviye: {result.risk.ai_risk_level?.toUpperCase()} · Skor: {result.risk.ai_risk_score}/100
              </p>
            </div>
          )}
          <div className="flex justify-end pt-2">
            <button
              type="button" onClick={apply} data-testid="ai-apply"
              className="bg-slate-900 text-white px-5 py-2 rounded-full font-medium text-xs hover:bg-black transition-colors"
            >
              Forma uygula
            </button>
          </div>
          {result.mocked && (
            <p className="text-[11px] text-amber-800">⚠ AI mock modunda çalıştı.</p>
          )}
        </div>
      )}
    </div>
  );
}
